import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';

import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Separator } from '../ui/separator';
import { useAuth } from '../../lib/AuthContext';
import exactaPageLogo from '../../assets/exactapage-logo-lightbg-vertical.svg';

const LoginForm = () => {
    const navigate = useNavigate();
    const { login, devModeLogin, isDevelopment } = useAuth();
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // This ref contains the login wrapper element
    const googleLoginWrapperRef = useRef(null);

    const handleGoogleLoginSuccess = async (credentialResponse) => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/auth', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    credential: credentialResponse.credential
                }),
            });

            const data = await response.json();

            if (response.status === 403) {
                setError("Your email address is not authorized to access this application. Please contact the administrator.");
                setIsLoading(false);
                return;
            }

            if (!response.ok) {
                throw new Error(data.error || 'Authentication failed');
            }

            // Use the login function from AuthContext to update state and localStorage
            login(data.user, data.token);

            // Redirect to homepage after successful login
            navigate('/');

        } catch (error) {
            console.error('Authentication error:', error);
            setError(error.message || "Failed to authenticate. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleLoginError = () => {
        console.error('Google login failed');
        setError('Google login failed. Please try again.');
    };

    const handleDevModeLogin = () => {
        devModeLogin();
        navigate('/');
    };

    // For testing purposes only - allows test frameworks to trigger the Google login success handler
    // from outside the component. This is a clean way to make the component testable.
    useEffect(() => {
        if (googleLoginWrapperRef.current) {
            // Listen for a custom event that tests can dispatch
            const handleTestEvent = (event) => {
                if (event.detail && event.detail.credential) {
                    handleGoogleLoginSuccess(event.detail);
                }
            };

            // Add a listener for tests to trigger
            googleLoginWrapperRef.current.addEventListener('googleLoginTest', handleTestEvent);

            // Clean up
            return () => {
                if (googleLoginWrapperRef.current) {
                    googleLoginWrapperRef.current.removeEventListener('googleLoginTest', handleTestEvent);
                }
            };
        }
    }, [googleLoginWrapperRef]);

    return (
        <Card className="w-full">
            <CardHeader className="space-y-1">
                <CardTitle className="text-2xl text-center">
                    <img
                        src={exactaPageLogo}
                        alt="Exactapage"
                        className="h-24 mx-auto"
                    />
                </CardTitle>
                <CardDescription className="text-center">
                    Sign in to access your admin dashboard
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
                {error && (
                    <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
                        {error}
                    </div>
                )}

                <div
                    ref={googleLoginWrapperRef}
                    className="flex justify-center cursor-pointer"
                    data-testid="google-login-wrapper"
                >
                    <GoogleLogin
                        onSuccess={handleGoogleLoginSuccess}
                        onError={handleGoogleLoginError}
                        size="large"
                        text="signin_with"
                        shape="pill"
                        disabled={isLoading}
                    />
                </div>

                {isLoading && (
                    <div className="text-center text-sm text-muted-foreground">
                        Authenticating...
                    </div>
                )}

                {isDevelopment && (
                    <>
                        <div className="relative my-4">
                            <div className="absolute inset-0 flex items-center">
                                <Separator />
                            </div>
                            <div className="relative flex justify-center text-xs uppercase">
                                <span className="bg-card px-2 text-muted-foreground">
                                    Development Mode
                                </span>
                            </div>
                        </div>

                        <Button
                            onClick={handleDevModeLogin}
                            className="w-full"
                            variant="outline"
                            disabled={isLoading}
                        >
                            Quick Sign In (Dev Mode)
                        </Button>
                    </>
                )}
            </CardContent>
            <CardFooter className="flex flex-col">
                <p className="mt-2 text-xs text-center text-muted-foreground">
                    By signing in, you agree to our Terms of Service and Privacy Policy.
                </p>
            </CardFooter>
        </Card>
    );
};

export default LoginForm; 